import { Backdrop, Box, Stack, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  SIDEBAR_TOTAL_WIDTH,
  SIDEPANEL_NAVBAR_WIDTH,
  CHAT_PANEL_WIDTH_DESKTOP,
  FULLSCREEN_SIDEBAR_WIDTH,
  Z_INDEX_SIDEBAR,
  Z_INDEX_FULLSCREEN,
  Z_INDEX_BACKDROP,
} from "./constants";
import { dumontColors } from "./colors";

export const SidebarContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 64,
  right: 0,
  width: SIDEBAR_TOTAL_WIDTH,
  height: "calc(100vh - 64px)",
  backgroundColor: "transparent",
  boxShadow: "0px 0px 5px 0px rgba(0, 0, 0, 0.1)",
  zIndex: Z_INDEX_SIDEBAR,
  transition: "transform 0.3s ease-in-out",
  transform: "translateX(0)",
  display: "flex",
  "&.closed": {
    transform: "translateX(100%)",
  },
  "&.fullscreen": {
    top: 64,
    right: 0,
    width: "100vw",
    height: "calc(100vh - 64px)",
    backgroundColor: dumontColors.white,
    zIndex: Z_INDEX_FULLSCREEN,
    boxShadow: "none",
    flexDirection: "row",
  },
  "&.embedded": {
    position: "relative",
    top: 0,
    right: "auto",
    width: "100%",
    height: "100%",
    minHeight: 0,
    transform: "none",
    boxShadow: "none",
  },
  "&.embedded.closed": {
    display: "none",
  },
  [theme.breakpoints.down("md")]: {
    width: 400,
  },
  [theme.breakpoints.down("sm")]: {
    width: 350,
    top: 56,
    height: "calc(100vh - 56px)",
  },
}));

export const SidepanelNavbar = styled(Box)(({ theme }) => ({
  width: SIDEPANEL_NAVBAR_WIDTH,
  height: "100%",
  backgroundColor: dumontColors.gray50,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  paddingTop: 12,
  "&.fullscreen": {
    display: "none",
  },
}));

export const AiChatContainer = styled(Box)(({ theme }) => ({
  width: CHAT_PANEL_WIDTH_DESKTOP,
  height: "100%",
  backgroundColor: dumontColors.white,
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  "&.fullscreen": {
    flex: 1,
    minWidth: 0,
    height: "100%",
    minHeight: 0,
    overflowY: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  [theme.breakpoints.down("md")]: {
    width: 330,
  },
  [theme.breakpoints.down("sm")]: {
    width: 280,
  },
}));

export const FullscreenSidebar = styled(Box)(({ theme }) => ({
  width: FULLSCREEN_SIDEBAR_WIDTH,
  flex: `0 0 ${FULLSCREEN_SIDEBAR_WIDTH}px`,
  height: "100%",
  minHeight: 0,
  backgroundColor: dumontColors.gray50,
  borderRight: `1px solid ${dumontColors.border}`,
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": { display: "none" },
}));

export const BackdropOverlay = styled(Backdrop)(({ theme }) => ({
  zIndex: Z_INDEX_BACKDROP,
  [theme.breakpoints.up("md")]: {
    display: "none",
  },
}));

export const ChatMessagesContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: "16px 24px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
}));

export const UserMessage = styled(Box)(({ theme }) => ({
  alignSelf: "flex-end",
  backgroundColor: dumontColors.primary,
  borderRadius: "8px",
  padding: "8px 12px 8px 12px",
  minHeight: "34.19px",
  maxWidth: "80%",
  color: dumontColors.white,
  display: "inline-flex",
  alignItems: "flex-start",
  boxSizing: "border-box",
  flexShrink: 0,
  width: "fit-content",
  wordWrap: "break-word",
  overflowWrap: "break-word",
  whiteSpace: "pre-wrap",
}));

export const DumontMessage = styled(Box)(({ theme }) => ({
  alignSelf: "flex-start",
  display: "flex",
  alignItems: "flex-start",
  gap: "12px",
  maxWidth: "90%",
}));

export const DumontAvatar = styled(Box)(({ theme }) => ({
  width: "32px",
  height: "32px",
  backgroundColor: "rgba(0, 164, 159, 0.05)",
  border: "1px solid rgba(0, 164, 159, 0.4)",
  borderRadius: "6px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
}));

export const DumontMessageBubble = styled(Box)(({ theme }) => ({
  backgroundColor: dumontColors.gray100,
  borderRadius: "8px",
  padding: "8px 12px 8px 12px",
  color: dumontColors.text,
  wordWrap: "break-word",
  overflowWrap: "break-word",
}));

export const HistoryContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  backgroundColor: dumontColors.white,
}));

export const HistoryHeader = styled(Box)(({ theme }) => ({
  height: "55.39px",
  borderBottom: `1px solid ${dumontColors.border}`,
  display: "flex",
  alignItems: "center",
  padding: "0 24px",
}));

export const HistoryContent = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: "0",
}));

export const HistoryItem = styled(Box)(({ theme }) => ({
  padding: "12px 0 12px 24px",
  borderBottom: `1px solid ${dumontColors.gray100}`,
  cursor: "pointer",
  height: "62.58px",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  boxSizing: "border-box",
  "&:hover": {
    backgroundColor: dumontColors.gray50,
  },
}));

export const SearchContainer = styled(Box)(({ theme }) => ({
  padding: "12px 24px 12px 24px",
  borderBottom: `1px solid ${dumontColors.border}`,
}));

export const SearchInput = styled(TextField)(({ theme }) => ({
  width: "389px",
  "& .MuiOutlinedInput-root": {
    backgroundColor: dumontColors.white,
    border: `1px solid ${dumontColors.border}`,
    borderRadius: "6px",
    padding: "8px 36px",
    height: "38px",
    "& fieldset": {
      border: "none",
    },
    "& .MuiInputBase-input": {
      fontSize: 13,
      fontWeight: 400,
      color: dumontColors.textTertiary,
      padding: "0",
      "&::placeholder": {
        color: dumontColors.textTertiary,
        opacity: 1,
      },
    },
  },
}));

export const AgentsContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  backgroundColor: dumontColors.white,
}));

export const AgentsHeader = styled(Box)(({ theme }) => ({
  height: "63px",
  borderBottom: `1px solid ${dumontColors.border}`,
  display: "flex",
  alignItems: "center",
  padding: "0 24px",
}));

export const AgentsSearchContainer = styled(Box)(({ theme }) => ({
  padding: "12px 24px 12px 24px",
  borderBottom: `1px solid ${dumontColors.border}`,
}));

export const AgentsContent = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: "16px 24px 0 24px",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
}));

export const AgentCard = styled(Box)(({ theme }) => ({
  border: `1px solid ${dumontColors.border}`,
  borderRadius: "8px",
  padding: "16px 0 16px 16px",
  marginBottom: "12px",
  cursor: "pointer",
  height: "126.39px",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  boxSizing: "border-box",
  "&:hover": {
    backgroundColor: dumontColors.gray50,
    borderColor: dumontColors.primary,
  },
}));

export const AgentIcon = styled(Box)(({ theme }) => ({
  width: "40px",
  height: "40px",
  backgroundColor: dumontColors.primaryLight,
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

export const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  padding: "16px 0",
  borderTop: `1px solid ${dumontColors.border}`,
  marginTop: "16px",
  height: "49px",
  width: "374px",
}));

export const PaginationButton = styled(Box)(({ theme }) => ({
  width: "32px",
  height: "32px",
  borderRadius: "6px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  fontSize: "12px",
  fontWeight: 500,
  "&.active": {
    backgroundColor: dumontColors.primary,
    color: dumontColors.white,
  },
  "&.inactive": {
    backgroundColor: dumontColors.white,
    border: `1px solid ${dumontColors.border}`,
    color: dumontColors.gray300,
  },
}));
